import React, { useState } from "react";
import { Link, NavLink } from "react-router-dom";
import { Globe, Menu, X, LogOut, Activity } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { useLanguage, LANGUAGES, T } from "../context/LanguageContext";

const links = [
  { to: "/", label: "Home" },
  { to: "/sports", label: "Explore Sports" },
  { to: "/countries", label: "Countries" },
  { to: "/teams", label: "Teams & Leagues" },
  { to: "/events", label: "Events & Scores" },
  { to: "/schools", label: "Schools & Colleges" },
  { to: "/health", label: "Sports Health" },
  { to: "/halloffame", label: "Halls of Fame" },
  { to: "/news", label: "News" },
  { to: "/articles", label: "Articles" },
  { to: "/business", label: "Business" },
  { to: "/community", label: "Community" },
];

export const Navbar = () => {
  const { user, login, logout } = useAuth();
  const { lang, setLang } = useLanguage();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [langOpen, setLangOpen] = useState(false);

  return (
    <header className="sticky top-0 z-50 border-b border-slate-200 bg-white/90 backdrop-blur-md">
      <div className="mx-auto flex h-16 max-w-[1400px] items-center justify-between gap-3 px-4 sm:px-6">
        <Link to="/" className="flex shrink-0 items-center gap-2" data-testid="nav-logo">
          <span className="flex h-9 w-9 items-center justify-center rounded-full bg-[#0055FF] text-white">
            <Activity className="h-5 w-5" />
          </span>
          <span className="text-lg font-extrabold tracking-tight text-slate-900">
            SportsPulse<span className="text-[#0055FF]">.pro</span>
          </span>
        </Link>

        <nav className="hidden items-center gap-0.5 xl:flex" data-testid="desktop-nav">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={`nav-link-${l.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
              className={({ isActive }) =>
                `rounded-full px-2.5 py-1.5 text-[13px] font-semibold transition-colors ${
                  isActive ? "text-[#0055FF]" : "text-slate-600 hover:text-slate-900"
                }`
              }
            >
              <T>{l.label}</T>
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-2">
          <div className="relative">
            <button
              data-testid="language-selector"
              onClick={() => setLangOpen((o) => !o)}
              className="inline-flex items-center gap-1.5 rounded-full border border-slate-300 bg-white px-3 py-1.5 text-xs font-bold uppercase text-slate-700 hover:border-[#0055FF] hover:text-[#0055FF]"
            >
              <Globe className="h-4 w-4" /> {lang}
            </button>
            {langOpen && (
              <div className="absolute right-0 mt-2 max-h-80 w-52 overflow-y-auto rounded-xl border border-slate-200 bg-white p-1 shadow-xl" data-testid="language-dropdown">
                {LANGUAGES.map((l) => (
                  <button
                    key={l.code}
                    data-testid={`language-option-${l.code}`}
                    onClick={() => { setLang(l.code); setLangOpen(false); }}
                    className={`flex w-full items-center justify-between rounded-lg px-3 py-1.5 text-left text-sm hover:bg-blue-50 ${
                      lang === l.code ? "font-bold text-[#0055FF]" : "text-slate-700"
                    }`}
                  >
                    {l.name} <span className="text-[10px] uppercase text-slate-400">{l.code}</span>
                  </button>
                ))}
              </div>
            )}
          </div>

          {user ? (
            <div className="flex items-center gap-2">
              {user.picture ? (
                <img src={user.picture} alt={user.name} className="h-8 w-8 rounded-full border border-slate-200" data-testid="user-avatar" />
              ) : (
                <span className="flex h-8 w-8 items-center justify-center rounded-full bg-[#0055FF] text-xs font-bold text-white">{user.name?.[0]}</span>
              )}
              <button data-testid="logout-button" onClick={logout} className="hidden items-center gap-1 rounded-full border border-slate-300 px-3 py-1.5 text-xs font-semibold text-slate-600 hover:border-red-400 hover:text-red-500 sm:inline-flex">
                <LogOut className="h-3.5 w-3.5" /> <T>Sign out</T>
              </button>
            </div>
          ) : (
            <button
              data-testid="join-button"
              onClick={login}
              className="hidden rounded-full bg-[#0055FF] px-4 py-2 text-sm font-bold text-white transition-colors hover:bg-[#0040CC] sm:block"
            >
              <T>Join SportsPulse</T>
            </button>
          )}

          <button data-testid="mobile-menu-button" onClick={() => setMobileOpen((o) => !o)} className="rounded-lg p-2 text-slate-700 xl:hidden">
            {mobileOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>

      {mobileOpen && (
        <nav className="border-t border-slate-200 bg-white px-4 py-3 xl:hidden" data-testid="mobile-nav">
          {links.map((l) => (
            <NavLink key={l.to} to={l.to} onClick={() => setMobileOpen(false)} className="block rounded-lg px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-blue-50">
              <T>{l.label}</T>
            </NavLink>
          ))}
          {!user && (
            <button onClick={login} className="mt-2 w-full rounded-full bg-[#0055FF] px-4 py-2 text-sm font-bold text-white">
              <T>Join SportsPulse</T>
            </button>
          )}
        </nav>
      )}
    </header>
  );
};
